import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { strings } from '@/data/strings';
import { Button } from '@/components/Button';
import { useActiveSection } from '@/hooks/useActiveSection';
import styles from './Navbar.module.css';

const NAV_LINKS = [
  { id: 'que-es', label: strings.nav.whatIs },
  { id: 'como-funciona', label: strings.nav.howItWorks },
  { id: 'para-profesionales', label: strings.nav.forProfessionals },
  { id: 'faq', label: strings.nav.faq },
] as const;

const SECTION_IDS = NAV_LINKS.map((l) => l.id);
// Referencia estable para no re-disparar el effect de useActiveSection fuera del landing.
const NO_SECTIONS: string[] = [];

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

// landing=true: anchors en-página (#x) + scroll-spy (uso original en la landing).
// landing=false: links absolutos al landing (/#x) y sin scroll-spy (páginas de contenido).
export function Navbar({ landing = false }: { landing?: boolean } = {}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const active = useActiveSection(landing ? SECTION_IDS : NO_SECTIONS);

  const hrefBase = landing ? '' : '/';
  const brandHref = landing ? '#top' : '/';
  const ctaHref = `${hrefBase}#descargar`;
  const linkHref = (id: string) => `${hrefBase}#${id}`;

  useEffect(() => {
    if (!isMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  return (
    <header className={styles.navbar}>
      <div className={styles.inner}>
        <a href={brandHref} className={styles.brand} onClick={closeMenu} aria-label={`${strings.brand.name} — ir al inicio`}>
          <span className={styles.brandText}>{strings.brand.name}</span>
          <span className={styles.brandDot} aria-hidden="true" />
        </a>

        <nav className={styles.nav} aria-label="Navegación principal">
          {NAV_LINKS.map((link) => {
            const isActive = active === link.id;
            return (
              <a
                key={link.id}
                href={linkHref(link.id)}
                className={cx(styles.link, isActive && styles.linkActive)}
                aria-current={isActive ? 'true' : undefined}
              >
                <span className={styles.linkLabel}>{link.label}</span>
              </a>
            );
          })}
        </nav>

        <div className={styles.actions}>
          <div className={styles.ctaDesktop}>
            <Button as="a" href={ctaHref} variant="primary" size="md">
              {strings.nav.downloadCta}
            </Button>
          </div>

          <a
            href={ctaHref}
            className={styles.ctaMobile}
            aria-label={strings.nav.downloadCta}
            onClick={closeMenu}
          >
            <svg
              className={styles.ctaMobileIcon}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3v12" />
              <path d="m7 10 5 5 5-5" />
              <path d="M5 21h14" />
            </svg>
          </a>

          <button
            type="button"
            className={cx(styles.hamburger, isMenuOpen && styles.hamburgerOpen)}
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMenuOpen}
            aria-controls="navbar-mobile-menu"
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
          </button>
        </div>
      </div>

      <div
        id="navbar-mobile-menu"
        className={cx(styles.mobileMenu, isMenuOpen && styles.mobileMenuOpen)}
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
        aria-hidden={!isMenuOpen}
      >
        <nav className={styles.mobileNav} aria-label="Navegación móvil">
          {NAV_LINKS.map((link, i) => (
            <a
              key={link.id}
              href={linkHref(link.id)}
              className={styles.mobileLink}
              style={{ '--i': i } as CSSProperties}
              onClick={closeMenu}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              <span className={styles.mobileLinkLabel}>{link.label}</span>
              <span className={styles.mobileLinkArrow} aria-hidden="true">→</span>
            </a>
          ))}

          <div
            className={styles.mobileCta}
            style={{ '--i': NAV_LINKS.length } as CSSProperties}
          >
            <Button
              as="a"
              href={ctaHref}
              variant="primary"
              size="lg"
              onClick={closeMenu}
            >
              {strings.nav.downloadCta}
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
}
